// ============================================
// FILE: /frontend/src/components/listings/RecentlyViewed.jsx
// PURPOSE: Recently viewed listings widget (fetches data from API using stored IDs)
// FIXED: Now fetches full listing data from API instead of expecting it in localStorage
// ============================================

import { useState, useEffect } from 'react';
import { Clock, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useNavigate } from 'react-router-dom';
import { listingsAPI } from '../../api/listings.api';
import { Skeleton } from '../ui/skeleton';

const MAX_RECENT_ITEMS = 5;

export default function RecentlyViewed({ currentListingId }) {
  const navigate = useNavigate();
  const [recentListings, setRecentListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

 useEffect(() => {
    let isCancelled = false;

    const loadRecentListings = async () => {
      try {
        setLoading(true);
        setError(null);

        const saved = localStorage.getItem('recentlyViewed');
        if (!saved) {
          if (!isCancelled) {
            setRecentListings([]);
            setLoading(false);
          }
          return;
        }

        const recentIds = JSON.parse(saved);
        const validIds = recentIds.filter((id) => id && typeof id === 'string' && id.trim() !== '');
        const filteredIds = validIds.filter((id) => id !== currentListingId);
        
        if (filteredIds.length === 0) {
          if (!isCancelled) {
            setRecentListings([]);
            setLoading(false);
          }
          return;
        }

        const idsToFetch = filteredIds.slice(0, MAX_RECENT_ITEMS);
        console.log('[RecentlyViewed] Fetching listings for IDs:', idsToFetch);

        const listingPromises = idsToFetch.map(async (id) => {
          try {
            const response = await listingsAPI.getListing(id);
            return response.listing;
          } catch (error) {
            console.error(`[RecentlyViewed] Failed to fetch listing ${id}:`, error);
            return null;
          }
        });

        const fetchedListings = await Promise.all(listingPromises);
        
        if (!isCancelled) {
          const validListings = fetchedListings.filter(listing => listing !== null);
          console.log('[RecentlyViewed] Valid listings loaded:', validListings.length);
          setRecentListings(validListings);
        }
      } catch (error) {
        if (!isCancelled) {
          console.error('[RecentlyViewed] Failed to load recent listings:', error);
          setError(error.message);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    loadRecentListings();

    return () => {
      isCancelled = true;
    };
  }, [currentListingId]);

  // Don't show anything if loading and no error
  if (loading && recentListings.length === 0) {
    return (
      <Card>
        <CardHeader className="p-3 sm:p-4 md:p-6">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg md:text-xl">
            <Clock className="w-4 h-4 sm:w-5 sm:h-5" />
            Recently Viewed
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 sm:space-y-3 p-3 sm:p-4 md:p-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex gap-2 sm:gap-3 p-2 sm:p-3 border rounded-lg">
              <Skeleton className="w-16 h-16 sm:w-20 sm:h-20 rounded" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-3 w-1/3" />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  // Don't render if no listings and not loading
  if (recentListings.length === 0 && !loading) return null;

  // Don't render if there's an error
  if (error) return null;

  return (
    <Card>
      <CardHeader className="p-3 sm:p-4 md:p-6">
        <CardTitle className="flex items-center gap-2 text-base sm:text-lg md:text-xl">
          <Clock className="w-4 h-4 sm:w-5 sm:h-5" />
          Recently Viewed
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-2 sm:space-y-3 p-3 sm:p-4 md:p-6">
       {recentListings.map((listing) => {
          // Get the first image from the array
          let imageUrl = Array.isArray(listing.imageUrl) && listing.imageUrl.length > 0
            ? listing.imageUrl[0]
            : listing.imageUrl;

          // ✅ FIX: Construct full URL if it's a backend upload path
          if (imageUrl && !imageUrl.startsWith('http')) {
            imageUrl = `${import.meta.env.VITE_BASE_URL || 'http://localhost:5000'}${imageUrl}`;
          }

          return (
            <div
              key={listing.id}
              onClick={() => navigate(`/listing/${listing.id}`)}
              className="flex gap-2 sm:gap-3 p-2 sm:p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
            >
              <div className="w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 rounded overflow-hidden bg-gray-200">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={listing.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = '/placeholder-car.jpg';
                    }}
                  />
                  
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                    No Image
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-xs sm:text-sm md:text-base truncate">
                  {listing.title}
                </h4>
                <p className="text-primary font-bold text-xs sm:text-sm md:text-base mt-0.5 sm:mt-1">
                  KES {parseFloat(listing.price).toLocaleString()}
                </p>
                <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5 truncate">
                  {listing.county}
                </p>
              </div>

              <div className="flex items-center">
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}